import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { fmtDate, apiErr } from "@/lib/format";
import { useLiveSearch, SearchBox } from "@/components/LiveSearch";
import { PlusIcon, HandshakeIcon, ClockIcon, CheckCircleIcon, WarningCircleIcon, ProhibitIcon } from "@phosphor-icons/react";

// Estado de la promesa: siempre con texto e icono (no solo color)
export const ESTADOS_PROMESA = {
  pendiente: { l: "Pendiente", i: ClockIcon, c: "bg-agro-yellow/10 text-agro-yellow border-agro-yellow/40" },
  vencida: { l: "Vencida", i: WarningCircleIcon, c: "bg-red-500/10 text-red-300 border-red-500/40" },
  cumplida: { l: "Cumplida", i: CheckCircleIcon, c: "bg-agro-green/10 text-agro-green border-agro-green/40" },
  cancelada: { l: "Cancelada", i: ProhibitIcon, c: "bg-white/5 text-text-secondary border-border" },
};

export function EstadoPromesa({ e }) {
  const s = ESTADOS_PROMESA[e] || ESTADOS_PROMESA.pendiente;
  const I = s.i;
  return <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded border ${s.c}`}><I size={12} weight="bold" /> {s.l}</span>;
}

export const montoPromesa = (p) => (Number(p.monto) > 0 ? `${p.moneda === "USD" ? "US$" : "L"} ${Number(p.monto).toLocaleString("es-HN", { minimumFractionDigits: 2 })}` : "—");

/** Promesas de pago (Ventas y facturación): RTC, Delegado y Administrador, dentro del alcance de cada perfil. */
export default function PromesasPago() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [estado, setEstado] = useState("");
  useEffect(() => {
    api.get("/promesas-pago").then(({ data: d }) => setData(d)).catch((e) => setError(apiErr(e)));
  }, []);
  const porEstado = useMemo(() => (data?.items || []).filter((p) => !estado || p.estado_visible === estado), [data, estado]);
  const busqueda = useLiveSearch(porEstado, (p) => [p.cliente_nombre, p.cliente_codigo, p.factura_numero, p.nota, p.responsable_nombre]);

  return (
    <div className="space-y-4" data-testid="promesas-pago">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-heading text-lg flex items-center gap-2"><HandshakeIcon size={20} className="text-agro-green" /> Promesas de pago</h3>
          <p className="text-xs text-text-secondary">Compromisos de pago acordados con clientes. No registran pagos ni cambian saldos; se ven también en el Calendario.</p>
        </div>
        <button type="button" onClick={() => navigate("/ventas/promesas/nueva")} data-testid="crear-promesa"
          className="min-h-[44px] bg-agro-green hover:bg-agro-green-hover text-black font-semibold rounded-md px-4 py-2 text-sm flex items-center gap-2">
          <PlusIcon size={16} weight="bold" /> Crear promesa de pago
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <SearchBox busqueda={busqueda} placeholder="Buscar por cliente, código, factura o compromiso" testid="promesas-buscar" />
        <div className="flex flex-wrap gap-1" role="group" aria-label="Filtrar por estado">
          {[["", "Todas"], ...Object.entries(ESTADOS_PROMESA).map(([k, v]) => [k, v.l])].map(([k, l]) => (
            <button key={k || "todas"} type="button" onClick={() => setEstado(k)} aria-pressed={estado === k} data-testid={`promesas-estado-${k || "todas"}`}
              className={`min-h-[36px] px-3 rounded-md border text-xs ${estado === k ? "bg-agro-green/15 border-agro-green/50 text-agro-green font-semibold" : "border-border text-text-secondary hover:text-text-primary"}`}>
              {l}{data?.resumen && k ? ` (${data.resumen[k] || 0})` : ""}
            </button>
          ))}
        </div>
      </div>

      {error ? <div className="text-sm text-red-300">{error}</div>
        : !data ? <div className="text-sm text-text-muted">Cargando promesas…</div>
        : busqueda.resultados.length === 0 ? (
          <div className="bg-card border border-border/60 rounded-xl p-8 text-center text-sm text-text-secondary" data-testid="promesas-vacio">
            {busqueda.activa ? `Sin resultados para «${busqueda.texto}».` : "Aún no hay promesas de pago registradas."}
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {busqueda.resultados.map((p) => (
              <button key={p.id} type="button" onClick={() => navigate(`/ventas/promesas/${p.id}`)} data-testid={`promesa-${p.id}`}
                className="text-left bg-card border border-border/60 rounded-xl p-4 hover:border-agro-green/50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-agro-green">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-semibold text-sm truncate">{p.cliente_nombre}</div>
                    <div className="text-[11px] text-text-muted">{p.cliente_codigo || ""}{p.factura_numero ? ` · ${p.factura_numero}` : ""}</div>
                  </div>
                  <EstadoPromesa e={p.estado_visible} />
                </div>
                <div className="mt-2 text-xs text-text-secondary line-clamp-2">{p.nota}</div>
                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs">
                  <span><span className="text-text-muted">Fecha comprometida:</span> <b>{fmtDate(p.fecha)}</b></span>
                  <span><span className="text-text-muted">Monto:</span> <b>{montoPromesa(p)}</b></span>
                  <span className="text-text-muted">Responsable: {p.responsable_nombre || p.created_by}</span>
                  {(p.reprogramaciones || []).length > 0 && <span className="text-text-muted">Reprogramada {p.reprogramaciones.length}×</span>}
                </div>
              </button>
            ))}
          </div>
        )}
    </div>
  );
}

